import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './PromptsPage.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../shared/routes/path';
import Loader from '../../shared/components/common/loader/Loader';
import PromptsTable, { PromptGroupItem } from '../../shared/components/prompts-table/PromptsTable';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import {
  usePromptGroups,
  useAssignPromptToGroup,
  useBulkAssignPromptsToGroup,
  useReorderPromptGroups,
} from '../../shared/hooks/prompt/UsePromptGroups';
import PromptGroupHeader from '../projects/preview/components/prompt-group-header/PromptGroupHeader';
import CreatePromptGroupDialog from '../projects/preview/components/create-prompt-group-dialog/CreatePromptGroupDialog';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import { PromptsData } from '../prompt/models/PromptsData';

const PromptsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);

  const { accountResponse, refetchAccountSilently } = useAccount();

  useEffect(() => {
    if (accountResponse && !accountResponse.addons.includes('PROMPT_GROUP')) {
      navigate(PROJECTS, { replace: true });
    }
  }, [accountResponse, navigate]);

  const { project, isLoading: isProjectLoading } = useGetProject(projectIdNumber);

  const {
    monitorPrompts,
    isLoading: isMonitorPromptsLoading,
    refetch: reloadMonitorPrompts,
  } = useGetMonitorsForProject(projectIdNumber);

  const { data: promptGroups } = usePromptGroups(projectIdNumber);
  const { mutate: assignPromptToGroup } = useAssignPromptToGroup(projectIdNumber, reloadMonitorPrompts);
  const { mutate: bulkAssignPromptsToGroup } = useBulkAssignPromptsToGroup(projectIdNumber, reloadMonitorPrompts);
  const { mutate: reorderPromptGroups } = useReorderPromptGroups(projectIdNumber);

  const [createPromptGroupVisible, setCreatePromptGroupVisible] = useState(false);

  const onRefresh = useCallback(async () => {
    await Promise.all([
      reloadMonitorPrompts(),
      refetchAccountSilently(),
    ]);
  }, [reloadMonitorPrompts, refetchAccountSilently]);

  const promptsData = useMemo<PromptsData[] | null>(() => {
    if (!monitorPrompts) return null;
    return monitorPrompts.map((prompt) => ({
      id: prompt.id,
      prompt: prompt.prompt,
      keyword: prompt.keyword,
      searchKeyword: prompt.searchKeyword,
      status: prompt.status,
      projectId: prompt.projectId,
      demoVersion: prompt.demoVersion,
      isRequiredAttention: prompt.isRequiredAttention,
      hasAiOverview: prompt.hasAiOverview,
      defaultPeriod: 'DAILY' as const,
      promptGroupId: prompt.promptGroupId,
      created: prompt.created,
    }));
  }, [monitorPrompts]);

  if (!accountResponse || isProjectLoading) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  if (!projectIdNumber || project?.demoVersion) {
    navigate(PROJECTS, { replace: true });
    return null;
  }

  return (
    <div className="prompts-page page-content">
      <div className="page-box">
        <CreatePromptGroupDialog
          visible={createPromptGroupVisible}
          onHide={() => setCreatePromptGroupVisible(false)}
          projectId={projectIdNumber}
        />
        <PromptsTable
          tableTitle={`${t('prompts')}`}
          data={promptsData}
          refreshAfterDelete={onRefresh}
          range={null}
          projectId={projectIdNumber}
          chatType="ALL"
          periodType={null}
          isLoading={isMonitorPromptsLoading}
          showStats={false}
          showKeyword={false}
          stickyHeader={true}
          groups={promptGroups as PromptGroupItem[] | undefined}
          hasPromptGroupsAddon={true}
          onAssignToGroup={(promptId, groupId) => assignPromptToGroup({ promptId, groupId })}
          onBulkAssignToGroup={(promptIds, groupId) => bulkAssignPromptsToGroup({ promptIds, groupId })}
          renderGroupHeader={(group, promptCount, isCollapsed, onToggleCollapse) => {
            const fullGroup = promptGroups?.find((g) => g.id === group.id);
            if (!fullGroup) return null;
            return (
              <PromptGroupHeader
                group={fullGroup}
                promptCount={promptCount}
                isCollapsed={isCollapsed}
                onToggleCollapse={onToggleCollapse}
                projectId={projectIdNumber}
                onDeleteSuccess={reloadMonitorPrompts}
              />
            );
          }}
          onReorderGroups={(orderedIds) => reorderPromptGroups(orderedIds)}
          onCreateGroup={() => setCreatePromptGroupVisible(true)}
        />
      </div>
    </div>
  );
};

export default PromptsPage;
